import React, { useState } from 'react';
import Lottie from 'lottie-react';
import dancingMonkeyData from './dancing-monkey.json';
import { useTelegram } from './TelegramProvider';
import { useTon } from './TelegramTonProvider';
import { useLilapeBalance } from './hooks/useLilapeBalance';
import { TelegramNavigation } from './TelegramNavigation';
import { TelegramWalletConnect } from './TelegramWalletConnect';
import { TonPurchaseModal } from './TonPurchaseModal';
import { TonStakingPage } from './TonStakingPage';
import { SlotMachineGame } from './SlotMachineGame';
import { AirdropPage } from './AirdropPage';
import { GamesHub } from './GamesHub';
import { CrashGame } from './CrashGame';
import { CoinFlip } from './CoinFlip';
import { DiceGame } from './DiceGame';
import { PlinkoGame } from './PlinkoGame';
import { HiLoGame } from './HiLoGame';
import { MinesGame } from './MinesGame';
import { ReferralPage } from './ReferralPage';
import TeamPage from './TeamPage';
import { TokenomicsPage } from './TokenomicsPage';
import { PresalePage } from './PresalePage';
import { VipPage } from './VipPage';
import { RoadmapPage } from './RoadmapPage';

const REFERRAL_KEY = 'lilape_referral_stats';
const REFERRAL_USED_KEY = 'lilape_referral_used';
const BONUS_SPINS_KEY = 'lilape_bonus_spins';

const LilapeInterface = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const { user } = useTelegram();
  const { connected, address } = useTon();
  const { formatted: lilapeBalance } = useLilapeBalance(connected ? address : null);

  // Handle deep links from website (game_slots, stake, presale, etc.)
  React.useEffect(() => {
    const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param || '';
    if (!startParam || startParam.startsWith('ref_')) return;

    // Map of valid deep link pages
    const validPages = [
      'game_slots', 'game_crash', 'game_coinflip', 'game_dice',
      'game_plinko', 'game_hilo', 'game_mines', 'game',
      'stake', 'presale', 'vip', 'tokenomics', 'roadmap', 'airdrop', 'referral'
    ];

    if (validPages.includes(startParam)) {
      setCurrentPage(startParam);
    }
  }, []);

  // Detect and process referral on first launch
  React.useEffect(() => {
    const processReferral = async () => {
      try {
        const alreadyUsed = localStorage.getItem(REFERRAL_USED_KEY);
        if (alreadyUsed) return;

        const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param || '';
        if (!startParam.startsWith('ref_')) return;

        const referrerId = startParam.replace('ref_', '');
        if (!referrerId || !user?.id || String(referrerId) === String(user.id)) return;

        // Mark as used immediately to prevent double-processing
        localStorage.setItem(REFERRAL_USED_KEY, '1');

        // Grant bonus spins to new user
        const currentSpins = parseInt(localStorage.getItem(BONUS_SPINS_KEY) || '0', 10);
        localStorage.setItem(BONUS_SPINS_KEY, String(currentSpins + 10));

        // Notify backend to reward referrer + log
        await fetch('/.netlify/functions/referral', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            referrerId,
            newUserId: String(user.id),
            newUserName: user.first_name || 'New Ape',
          }),
        });

        // Update referrer's local stats (if they open on same device — best-effort)
        try {
          const stored = JSON.parse(localStorage.getItem(REFERRAL_KEY) || '{"referralCount":0,"bonusSpinsEarned":0}');
          stored.referralCount += 1;
          stored.bonusSpinsEarned += 5;
          localStorage.setItem(REFERRAL_KEY, JSON.stringify(stored));
        } catch {}
      } catch (err) {
        console.warn('Referral processing error:', err.message);
      }
    };

    if (user?.id) processReferral();
  }, [user?.id]);

  const renderPage = () => {
    switch (currentPage) {

      /* ── TOKENOMICS ── */
      case 'tokenomics':
        return <TokenomicsPage />;

      /* ── PRESALE ── */
      case 'presale':
        return <PresalePage />;

      /* ── VIP ── */
      case 'vip':
        return <VipPage />;

      /* ── ROADMAP ── */
      case 'roadmap':
        return <RoadmapPage />;

      /* ── TEAM ── */
      case 'team':
        return <TeamPage />;

      /* ── AIRDROP ── */
      case 'airdrop':
        return <AirdropPage />;

      /* ── REFERRAL ── */
      case 'referral':
        return <ReferralPage />;

      /* ── GAMES HUB ── */
      case 'game':
        return <GamesHub onSelectGame={(id) => setCurrentPage('game_' + id)} />;

      /* ── SLOTS ── */
      case 'game_slots':
        return <SlotMachineGame />;

      /* ── CRASH ── */
      case 'game_crash':
        return <CrashGame onBack={() => setCurrentPage('game')} />;

      /* ── COIN FLIP ── */
      case 'game_coinflip':
        return <CoinFlip onBack={() => setCurrentPage('game')} />;

      /* ── DICE ── */
      case 'game_dice':
        return <DiceGame onBack={() => setCurrentPage('game')} />;

      /* ── PLINKO ── */
      case 'game_plinko':
        return <PlinkoGame onBack={() => setCurrentPage('game')} />;

      /* ── HILO ── */
      case 'game_hilo':
        return <HiLoGame onBack={() => setCurrentPage('game')} />;

      /* ── MINES ── */
      case 'game_mines':
        return <MinesGame onBack={() => setCurrentPage('game')} />;

      /* ── STAKE ── */
      case 'stake':
        return <TonStakingPage />;

      /* ── BUY ── */
      case 'buy':
        return (
          <div>
            <div className="page-header">
              <div className="page-header-icon blue">💎</div>
              <div>
                <div className="page-title">Buy LILAPE</div>
                <div className="page-subtitle">Direct purchase with TON</div>
              </div>
            </div>

            <div className="stats-row">
              {[
                { v: '420T+', l: 'Total Supply' },
                { v: '0%',    l: 'Tax Fee'      },
                { v: '100%',  l: 'Community'    },
              ].map(s => (
                <div key={s.l} className="stat-card">
                  <div className="stat-value">{s.v}</div>
                  <div className="stat-label">{s.l}</div>
                </div>
              ))}
            </div>

            <div className="rate-card">
              <div className="rate-row">
                <span className="rate-label">Exchange rate</span>
                <span className="rate-value">1 TON = 5B LILAPE</span>
              </div>
              <div className="rate-row">
                <span className="rate-label">Minimum</span>
                <span className="rate-value gold">0.1 TON</span>
              </div>
              <div className="rate-row">
                <span className="rate-label">Network</span>
                <span className="rate-value">TON Blockchain</span>
              </div>
            </div>

            <button
              className="buy-btn-primary"
              onClick={() => setShowPurchaseModal(true)}
              disabled={!connected}
            >
              {connected ? '💎 Buy LILAPE' : '🔗 Connect Wallet First'}
            </button>

            {!connected && (
              <div style={{ textAlign: 'center', padding: '0 16px 8px', fontSize: 12, color: 'var(--text-muted)' }}>
                Connect a TON wallet to buy here
              </div>
            )}

            {/* BASE chain cross-link */}
            <div style={{ margin: '4px 16px 16px', padding: 16, borderRadius: 'var(--radius-md)', background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>
                  🔗 Buy on BASE blockchain
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  DEX · MetaMask · Coinbase Wallet
                </div>
              </div>
              <a
                href="https://liteape.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ padding: '9px 14px', borderRadius: 10, background: 'rgba(37,99,235,0.18)', border: '1px solid rgba(37,99,235,0.35)', color: '#60A5FA', fontSize: 12, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                liteape.com →
              </a>
            </div>
          </div>
        );

      /* ── NFT ── */
      case 'nft':
        return (
          <div>
            <div className="page-header">
              <div className="page-header-icon gold">🖼️</div>
              <div>
                <div className="page-title">LILAPE NFTs</div>
                <div className="page-subtitle">Genesis Collection · 1,000 pieces</div>
              </div>
            </div>

            <div className="nft-coming-soon">
              <div style={{ fontSize: 60, marginBottom: 16 }}>🦍</div>
              <div className="coming-badge"><span>🔥</span> Coming to Getgems</div>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 12, lineHeight: 1.6 }}>
                1,000 unique animated NFTs.<br />
                Holders get 2× APY bonus in staking.
              </div>
              <div style={{ marginTop: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, textAlign: 'left' }}>
                {[
                  { icon: '💎', label: 'Mint Price', v: '5 TON'    },
                  { icon: '📦', label: 'Collection',  v: '1,000 NFT' },
                  { icon: '🏆', label: 'Royalties',   v: '5%'       },
                  { icon: '⚡', label: 'Staking Bonus', v: '×2 APY' },
                ].map(i => (
                  <div key={i.label} className="card" style={{ padding: '14px' }}>
                    <div style={{ fontSize: 20, marginBottom: 4 }}>{i.icon}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{i.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--gold)' }}>{i.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      /* ── HOME ── */
      default:
        return (
          <div>
            <div className="home-hero">
              <div style={{ textAlign: 'center' }}>
                <div className="hero-badge">
                  <span className="live-dot"></span>
                  🚀 Live on Telegram — TON Integration
                </div>
              </div>

              <h1 className="hero-title" style={{ textAlign: 'center' }}>
                <span className="gradient-text">The Wildest Meme Coin</span><br />
                <span>on TON</span><br />
                <span style={{ fontSize: 34 }}>🦍 Little APE</span>
              </h1>

              <p className="hero-subtitle" style={{ textAlign: 'center', padding: '0 8px' }}>
                The meme revolution on TON! From tiny ape to crypto king.
              </p>

              <div className="hero-ape-container">
                <div className="hero-ape-glow"></div>
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="hero-ape-lottie"
                  style={{ width: 220, height: 220, objectFit: 'cover', borderRadius: '50%' }}
                >
                  <source src="./ape.mp4" type="video/mp4" />
                  <source src="./dancing-gorilla.webm" type="video/webm" />
                  <source src="./dancing-gorilla-small.mp4" type="video/mp4" />
                </video>
              </div>

              {user && (
                <div className="glass-card" style={{ margin: '0 0 20px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontSize: 28 }}>👋</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>Hey, {user.first_name}!</div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Welcome to the LILAPE universe</div>
                  </div>
                </div>
              )}

              {connected && lilapeBalance !== null && (
                <div style={{ margin: '0 0 20px', padding: '14px 18px', background: 'linear-gradient(135deg,rgba(245,158,11,0.12),rgba(239,68,68,0.08))', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 26 }}>🦍</div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Your LILAPE Balance</div>
                      <div style={{ fontSize: 20, fontWeight: 900, color: 'var(--gold)' }}>{lilapeBalance}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right' }}>
                    <div>LILAPE</div>
                    <div>TON Chain</div>
                  </div>
                </div>
              )}
            </div>

            <div className="stats-row">
              {[
                { v: '420T+', l: 'Total Supply' },
                { v: '0%',    l: 'Tax Fee'      },
                { v: '100%',  l: 'Community'    },
              ].map(s => (
                <div key={s.l} className="stat-card">
                  <div className="stat-value">{s.v}</div>
                  <div className="stat-label">{s.l}</div>
                </div>
              ))}
            </div>

            <div className="features-grid">
              <div className="feature-card purple" onClick={() => setCurrentPage('game')}>
                <span className="feature-icon">🎮</span>
                <div className="feature-title">Games</div>
                <div className="feature-desc">Win up to x100 · 7 games</div>
              </div>
              <div className="feature-card gold" onClick={() => setCurrentPage('presale')}>
                <span className="feature-icon">🚀</span>
                <div className="feature-title">Presale</div>
                <div className="feature-desc">50% OFF · Stage 1</div>
              </div>
              <div className="feature-card" style={{background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(239,68,68,0.1))'}} onClick={() => setCurrentPage('vip')}>
                <span className="feature-icon">👑</span>
                <div className="feature-title">VIP Club</div>
                <div className="feature-desc">Up to x3.9 multiplier</div>
              </div>
              <div className="feature-card green" onClick={() => setCurrentPage('roadmap')}>
                <span className="feature-icon">🗺️</span>
                <div className="feature-title">Roadmap</div>
                <div className="feature-desc">Our journey ahead</div>
              </div>
              <div className="feature-card cyan" onClick={() => setCurrentPage('tokenomics')}>
                <span className="feature-icon">📊</span>
                <div className="feature-title">Tokenomics</div>
                <div className="feature-desc">420T supply · 50% burn</div>
              </div>
              <div className="feature-card" style={{background: 'linear-gradient(135deg, rgba(74,222,128,0.15), rgba(34,197,94,0.1))'}} onClick={() => setCurrentPage('team')}>
                <span className="feature-icon">👥</span>
                <div className="feature-title">Team</div>
                <div className="feature-desc">Meet the builders</div>
              </div>
            </div>

            {/* Referral CTA */}
            <div
              onClick={() => setCurrentPage('referral')}
              style={{
                margin: '0 16px 12px',
                padding: '16px',
                borderRadius: 'var(--radius-md)',
                background: 'linear-gradient(135deg,rgba(139,92,246,0.15),rgba(236,72,153,0.1))',
                border: '1px solid rgba(139,92,246,0.35)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
              }}
            >
              <div style={{ fontSize: 36 }}>👥</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#a78bfa', marginBottom: 2 }}>Invite Friends → Earn Spins</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>You get +5 spins · Friend gets +10 spins · No limit</div>
              </div>
              <div style={{ fontSize: 18, color: '#a78bfa' }}>›</div>
            </div>

            <div
              className="giveaway-banner"
              onClick={() => setCurrentPage('airdrop')}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ fontSize: 28, marginBottom: 8 }}>🎁</div>
              <div className="giveaway-title">10T LILAPE AIRDROP</div>
              <div className="giveaway-sub">
                Prize pool <strong style={{ color: 'var(--gold)' }}>10T LILAPE</strong> — tap to register your wallet and claim free tokens!
              </div>
              <div style={{ marginTop: 10, display: 'inline-block', padding: '6px 16px', background: 'rgba(245,158,11,0.2)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 50, fontSize: 12, fontWeight: 700, color: 'var(--gold)' }}>
                Register Now →
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', color: 'var(--text-primary)' }}>
      <div className="telegram-header">
        <div className="header-content">
          <div className="logo">
            <div className="logo-icon">🦍</div>
            <span className="logo-text">LILAPE</span>
          </div>
          <TelegramWalletConnect />
        </div>
      </div>

      <div className="telegram-main-content">
        {renderPage()}
      </div>

      <TelegramNavigation
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />

      <TonPurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
      />
    </div>
  );
};

export { LilapeInterface };
